import { Component, computed, DestroyRef, HostBinding, inject, input, InputSignal, linkedSignal, output, OutputEmitterRef, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TListView } from 'src/app/features/shared/central-page/models/list-view';
import { HttpParams } from '@angular/common/http';
import { Observable, catchError, finalize, EMPTY } from 'rxjs';

import { ActivatedRouteService } from '../../services/route-service/activated-route.service';
import { ApiService } from '../../services/api-service/api.service';
import { ClaimPage } from '../../models/claim-page';
import { CssVariablesService } from '../../services/css-variables/css-variables.service';
import { ITabDataTemplate } from 'src/app/features/shared/central-page/models/tab-data-template';
import { ILevelGeneralListData } from '../../models/level-general-list-data';
import { ILevelGeneralListDto } from '../../models/level-general-list-dto';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { TabDataService } from 'src/app/features/shared/central-page/services/tab-data.service';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { ApplicationUserService } from '../../services/application-user.services/application-user.service';
import { _ListService } from '../../services/list-services/_list.service';
import { _PageService } from '../../services/page-services/_page.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { LoadingTabService } from '../../services/loading-services/loading-tab.service';

@Component({
    selector: 'app-source-list-base-feature-component',
    template: '',
    styles: [],
})
export abstract class _SourceList_Base_FeatureComponent<ListDto extends ILevelGeneralListDto, ListData extends ILevelGeneralListData>
{
    protected abstract readonly _listService: _ListService<ListDto, ListData>;
    protected abstract readonly _pageService: _PageService;
    protected abstract readonly _pagerService: PagerService;
    protected abstract readonly _tabDataService: TabDataService;
    protected abstract readonly _tabListService: TabListService;

    abstract claimPageList$$: Signal<ClaimPage | null>;
    abstract itemLines?: number;

    abstract input_claimPageList$$: InputSignal<ClaimPage | null>;
    abstract input_pageSize$$: InputSignal<number>;

    abstract out_onClick: OutputEmitterRef<void>;
    abstract out_onPageLoaded: OutputEmitterRef<void>;
    abstract out_onSave: OutputEmitterRef<void>;

    hasImageBox: boolean = false;

    get ItemSize(): number {
        const __lineHeight = this.__cssVariablesService.lineHeight$$();
        const __imageRowHeight = this.__cssVariablesService.imageRowHeight$$();
        const __lines = this.itemLines ?? 1;
        return this.hasImageBox
            ? Math.max(__lineHeight * __lines, __imageRowHeight)
            : __lineHeight * __lines;
    }

    abstract httpParams$$: Signal<HttpParams | null>;
    abstract pageSize$$: Signal<number>;
    abstract requestName$$: Signal<string | null>;
    abstract tabIndex$$: Signal<number>;

    protected abstract _list_get$(): Observable<ListDto[]>;

    abstract ngOnInit(): void;

    private readonly __backgroundApiService = injectRequired(ApiService);
    private readonly __cssVariablesService = injectRequired(CssVariablesService);

    protected readonly _applicationUserService = injectRequired(ApplicationUserService);
    protected readonly _activatedRouteService = injectRequired(ActivatedRouteService);
    private readonly __loadingTabService = injectRequired(LoadingTabService);
    private readonly __destroyRef = inject(DestroyRef);

    protected _computed_httpParams$$ = computed(() => (this._tabListService.tabList$$()[this._computed_tabIndex$$()] as ITabDataTemplate)?.httpParams);
    protected _computed_requestName$$ = computed(() => (this._tabListService.tabList$$()[this._computed_tabIndex$$()] as ITabDataTemplate)?.requestName);
    // Weergave-versie van DEZE lijst (TabDataTemplate.listView). Een lijst-instantie hoort bij één tab,
    // maar tabIndex volgt de actieve tab: bij het wisselen rekent de oude lijst nog één keer door met
    // de nieuwe tab (zonder/met andere listView). Daarom blijft de eerste geldige waarde staan.
    protected _computed_listView$$ = linkedSignal<TListView | null, TListView | null>({
        source: () => (this._tabListService.tabList$$()[this._computed_tabIndex$$()] as ITabDataTemplate)?.listView ?? null,
        computation: (listView, previous) => previous?.value ?? listView,
    });
    protected _computed_save$$ = computed(() => (this._tabListService.tabList$$()[this._computed_tabIndex$$()] as ITabDataTemplate)?.save$ ?? null);
    protected _computed_tabIndex$$ = computed(() => this._tabListService.tabIndex$$());
    protected _pageSize$$ = signal<number | null>(null);

    protected _input_claimPageList$$ = input<ClaimPage | null>(null, {alias: 'claimPageList'});
    protected _input_pageSize$$ = input<number>(0, {alias: 'pageSize'});

    protected _out_onClick = output<void>({alias: 'onClick'});
    protected _out_onPageLoaded = output<void>({alias: 'onPageLoaded'});
    protected _out_onSave = output<void>({alias: 'onSave'});


    @HostBinding('id') get id() {return this._tabDataService.tabName$$();}
    @HostBinding('class') class = 'list-component';

    // Overridebaar: false laat de initiële (en ververs-)load achterwege, bijv. voor checklists die
    // hun volledige kandidatenlijst pas nodig hebben zodra edit-modus wordt geactiveerd (zie
    // _CheckList_Base_FeatureComponent). Default true behoudt het bestaande eager-load-gedrag
    // voor gewone tablists (Function/Page-tablist e.d.).
    protected _list_loadEnabled(): boolean {
        return true;
    }

    protected _ngOnInit(): void {
        this._pageSize$$.set(this._input_pageSize$$());
        if (this._list_loadEnabled()) {
            this._triggerLoad();
        } else {
            this._emit_onPageLoaded();
            // Pagina toont meteen (geen spinner/blokkade), maar de kandidatenlijst hoeft niet op
            // een edit-klik te wachten: op de achtergrond alvast prefetchen zodat de cache meestal
            // al gevuld is tegen de tijd dat edit-modus daadwerkelijk wordt geactiveerd. setTimeout(0)
            // laat dit ná de huidige render-tick starten i.p.v. gelijktijdig met de kritieke
            // requests van de pagina zelf (zelfde patroon als _ngAfterViewInit hieronder in de
            // tool-component).
            setTimeout(() => this._triggerLoad_background(), 0);
        }

        // tabList_setRefresh() zet needRefresh, maar dat wordt normaal alleen gelezen bij een
        // nieuwe mount (zie _list.service.ts._list_load$). Onder preserveContent blijft een
        // niet-actieve tab gemount en zou dat moment nooit meer komen — deze subscription vangt
        // dat specifiek voor de NIET-actieve tab op (de actieve tab heeft voor foto's al zijn
        // eigen, minder ingrijpende ververs-pad via _photoListChanged$, zie ImageTablistComponent).
        this._tabListService.tabRefresh$
            .pipe(takeUntilDestroyed(this.__destroyRef))
            .subscribe(() => {
                const __activeTabName = this._tabListService.tabList$$()[this._tabListService.tabIndex$$()]?.name;
                if (__activeTabName !== this._tabDataService.TabName && this._list_loadEnabled()) this._triggerLoad();
            });
    }

    protected _emit_onPageLoaded(): void {
        this.__loadingTabService.remove();
        this._out_onPageLoaded.emit();
    }

    // Per-tab load-status (loadingTab$$ in de tool-component) i.p.v. de pagina-brede loadingPage$$:
    // die laatste is na de initiële page-load blijvend false, ook als deze tab nu pas (initieel, of
    // via een latere tabList_setRefresh terwijl hij niet-actief gemount blijft) zijn lijst ophaalt.
    protected _triggerLoad(): void {
        this.__backgroundLoadStarted = true;
        this.__loadingTabService.add();
        this.__list_load$().subscribe();
    }

    // true zodra er ergens een load is gestart (voorgrond via _triggerLoad, of achtergrond
    // hieronder) — voorkomt dat de achtergrond-prefetch alsnog vuurt nadat edit-modus 'm al
    // (voortijdig) heeft ingehaald. Geen bescherming tegen de omgekeerde volgorde: als edit-modus
    // wordt geactiveerd terwijl de achtergrond-fetch nog in flight is, start _triggerLoad() gewoon
    // een eigen GET — bewust geaccepteerd, onschadelijk dubbel verzoek in een smal tijdvenster.
    private __backgroundLoadStarted = false;

    // Fire-and-forget variant van _triggerLoad(): vult dezelfde _ListService-cache, maar raakt
    // de tab-loading-indicator niet aan en emit niet nogmaals onPageLoaded (dat is bij het tonen
    // van de pagina al gebeurd) — puur een achtergrond-prefetch, zonder invloed op de UI.
    // ApiService.background: de pagina wacht hier niet op, dus ook geen footer-progressbar (een
    // grote kandidatenlijst, bv. alle claims op ActivityDetails, liet die anders verschijnen). De
    // factory start de GET synchroon binnen list_load$, dus valt binnen de scope.
    protected _triggerLoad_background(): void {
        if (this.__backgroundLoadStarted) return;
        this.__backgroundLoadStarted = true;

        this.__backgroundApiService.background(() => this._listService.list_load$(() => this._list_get$()))
            .pipe(catchError(() => EMPTY))
            .subscribe();
    }

    private __list_load$(): Observable<any[]> {
        return this._listService.list_load$(() => this._list_get$()).pipe(
            // Fouten zijn al centraal gerapporteerd door de transport-laag (api.service / interceptor);
            // hier alleen de flow afronden (page-loaded via finalize), niet opnieuw rapporteren.
            catchError(() => EMPTY),
            finalize(() => {
                this._emit_onPageLoaded();
            })
        );
    }
}
